import Head from "next/head";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { useCurrentPlatform } from "~/api/resources";
import { mediaUrl } from "~/lib/media";
import { SeoPageData } from "~/types";

type Props = {
  seoData?: SeoPageData;
};

const SITE_ORIGIN = "https://hwy3bc.ca";

const normalizeHttpUrl = (value?: string) => {
  if (!value?.trim()) return undefined;
  const trimmed = value.trim();
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const Seo = ({ seoData }: Props) => {
  const { asPath } = useRouter();
  const { data: platform } = useCurrentPlatform();
  const {
    metaTitle = "Highway 3",
    metaDescription = "Official website for travel planning Highway 3 from Hope to Elkford, BC",
    metaImage,
  } = seoData ?? {};

  const title = metaTitle || platform?.name || "Highway 3";
  const description =
    metaDescription ||
    platform?.description ||
    platform?.subtitle ||
    "Official website for travel planning Highway 3 from Hope to Elkford, BC";
  const image =
    mediaUrl(metaImage) ||
    mediaUrl(platform?.logo) ||
    `${SITE_ORIGIN}/meta_image.jpg`;
  const url =
    typeof window === "undefined"
      ? `${SITE_ORIGIN}${asPath}`
      : window.location.href;

  const sameAs = useMemo(
    () =>
      (platform?.links ?? [])
        .map((link) => normalizeHttpUrl(link.value))
        .filter((href): href is string => !!href),
    [platform?.links],
  );

  const organizationSchema = useMemo(() => {
    if (!platform) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: platform.name,
      url: SITE_ORIGIN,
      ...(description ? { description } : {}),
      ...(image ? { logo: image } : {}),
      ...(sameAs.length > 0 ? { sameAs } : {}),
    };
  }, [platform, description, image, sameAs]);

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} key="description" />

      <meta property="og:url" content={url} key="og:url" />
      <meta property="og:title" content={title} key="og:title" />
      <meta
        property="og:description"
        content={description}
        key="og:description"
      />
      <meta property="og:image" content={image} key="og:image" />
      <meta property="og:type" content="website" />
      <link rel="canonical" href={url} />

      <meta
        name="twitter:card"
        content="summary_large_image"
        key="twitter:card"
      />
      <meta name="twitter:title" content={title} key="twitter:title" />
      <meta
        name="twitter:description"
        content={description}
        key="twitter:description"
      />
      <meta name="twitter:image" content={image} key="twitter:image" />

      {organizationSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(organizationSchema),
          }}
        />
      )}
    </Head>
  );
};
